import { isMetaChannel, isWindowGated, type Channel } from "./types";
import { evaluateWindowGate, windowState, STANDARD_WINDOW_MS } from "./window";
import { OWED_CLOSED_PAGE_HORIZON_MS } from "./followUp";

/**
 * TA-1193 (F-01, F-02): a customer waiting on a draft the owner has not
 * released. One page when the Meta window enters grace, so the owner can still
 * answer in the inbox; for a `blocked` draft (nobody can approve it) one more
 * when the window closes. A `pending_approval` draft's close page is the
 * escalation sweep's own `escalation_window_closed`, so none is added here.
 * Non-Meta channels and comment replies are not window governed (F-20) and
 * never page. The window logic is window.ts, read and never re-derived. An
 * operator page only: it sends nothing.
 */
export const DRAFT_WAITING_PAGE_CATEGORY = "window_closing" as const;

export type DraftWaitingPageStage = "grace" | "closed";
export type DraftWaitingPageKind = "draft_waiting_window_grace" | "draft_waiting_window_closed";

export interface DraftWaitingPageInput {
  readonly channel: Channel;
  /** The draft replies to a public comment: not window gated. */
  readonly isComment: boolean;
  readonly draftStatus: string;
  readonly lastCustomerMessageAt: Date | null;
  readonly now: Date;
}

export type DraftWaitingPageDecision =
  | {
      readonly page: false;
      readonly reason:
        | "not_window_gated"
        | "comment"
        | "status_not_waiting"
        | "no_customer_message"
        | "in_window"
        | "closed_owned_by_escalation"
        | "closed_past_horizon";
    }
  | { readonly page: true; readonly stage: DraftWaitingPageStage; readonly kind: DraftWaitingPageKind };

export function planDraftWaitingPage(input: DraftWaitingPageInput): DraftWaitingPageDecision {
  if (!isWindowGated(input.channel)) return { page: false, reason: "not_window_gated" };
  if (input.isComment) return { page: false, reason: "comment" };
  if (input.draftStatus !== "pending_approval" && input.draftStatus !== "blocked") return { page: false, reason: "status_not_waiting" };
  if (input.lastCustomerMessageAt === null) return { page: false, reason: "no_customer_message" };
  const state = windowState({ lastCustomerMessageAt: input.lastCustomerMessageAt, now: input.now });
  if (state === "in_window") return { page: false, reason: "in_window" };
  if (state === "grace") return { page: true, stage: "grace", kind: "draft_waiting_window_grace" };
  if (input.draftStatus === "pending_approval") return { page: false, reason: "closed_owned_by_escalation" };
  const sinceClose = input.now.getTime() - input.lastCustomerMessageAt.getTime() - STANDARD_WINDOW_MS;
  if (sinceClose >= OWED_CLOSED_PAGE_HORIZON_MS) return { page: false, reason: "closed_past_horizon" };
  return { page: true, stage: "closed", kind: "draft_waiting_window_closed" };
}

/**
 * Escalation timing under the messaging window (F-02 RULING, Composer §6).
 *
 * Two specs collide here and the ruling settles which wins:
 *
 *   Agent Brains §12: at 15 minutes unanswered, the agent sends a pre-approved
 *   holding message, because "escalation is never a place a customer waits in
 *   silence". At 60 minutes, amber on the team board.
 *
 *   Composer §6: free-form DMs only within 24h of the customer's last message.
 *   Outside it, nothing free-form, ever.
 *
 * A holding message IS a free-form DM. When the SLA fires outside the window,
 * §12 asks for the exact thing §6 forbids, and the forbidden thing is
 * account-restriction territory, which is existential on Meta rails.
 *
 * The ruling: the window wins. The silence stops being something the customer
 * experiences unexplained and becomes something the OWNER is loudly told
 * about. Nobody is left waiting in silence; the party who can act is the one
 * who hears about it.
 */

/** B6 v1: human unanswered 15 min -> holding message. */
export const HOLDING_SLA_MS = 15 * 60_000;
/** B6 v1: 60 min -> amber on the team board. */
export const AMBER_SLA_MS = 60 * 60_000;

export type EscalationAction =
  | "wait"
  | "send_holding_message"
  | "capture_contact_before_close"
  | "fallback_to_sms"
  | "alert_owner_and_stay_silent";

export interface EscalationInput {
  readonly channel: Channel;
  readonly escalatedAt: Date;
  readonly lastCustomerMessageAt: Date | null;
  readonly now: Date;
  /** A phone or email we could actually use. */
  readonly hasContactOnFile: boolean;
  /**
   * Express consent recorded at capture. F-20: SMS and email have their own
   * regime, so a captured number is NOT permission by itself.
   */
  readonly hasSmsConsent: boolean;
  readonly holdingMessageSent: boolean;
}

export interface EscalationPlan {
  readonly action: EscalationAction;
  /** True when the window, not the SLA, decided the outcome. */
  readonly windowBlocked: boolean;
  readonly ownerAlert: "none" | "normal" | "loud";
  readonly teamBoard: "clear" | "amber";
  readonly notes: readonly string[];
}

export function planEscalation(input: EscalationInput): EscalationPlan {
  const notes: string[] = [];
  const waiting = input.now.getTime() - input.escalatedAt.getTime();
  const teamBoard: EscalationPlan["teamBoard"] = waiting >= AMBER_SLA_MS ? "amber" : "clear";

  const gate = evaluateWindowGate(
    { lastCustomerMessageAt: input.lastCustomerMessageAt, now: input.now },
    { kind: "freeform", channel: input.channel, hasMarketingOptIn: false },
  );

  if (!isWindowGated(input.channel)) {
    // F-20: name the fact that this function did not gate, rather than let a
    // silent pass read as approval.
    //
    // TA-283: and name it ACCURATELY. A public comment is not window gated, but
    // it is very much a Meta channel, so the old single note called it
    // "non_meta_channel" and told an auditor the opposite of the truth. The two
    // reasons a thread reaches here are different in kind: SMS and email are
    // off Meta's rails entirely and answer to their own consent regime, while a
    // comment rides Meta's rails and is ungated only because the reply is
    // public. Reading "not applicable" on a comment and concluding Meta has no
    // say is how a private reply gets sent on day 8, or twice.
    notes.push(
      isMetaChannel(input.channel)
        ? "public_meta_channel_window_not_applicable"
        : "non_meta_channel_window_not_applicable",
    );
  }

  const state = windowState({
    lastCustomerMessageAt: input.lastCustomerMessageAt,
    now: input.now,
  });

  // --- the window has closed: nothing free-form leaves, whatever the SLA says
  if (isWindowGated(input.channel) && !gate.allowed) {
    notes.push(`window_${state}`, "holding_message_blocked_by_window");

    if (input.hasContactOnFile && input.hasSmsConsent) {
      return {
        action: "fallback_to_sms",
        windowBlocked: true,
        ownerAlert: "normal",
        teamBoard,
        notes,
      };
    }
    if (input.hasContactOnFile) {
      // Captured but not consented. F-20 is explicit that capture alone is not
      // permission, and reaching for the number anyway is the exact shortcut
      // that regime exists to stop.
      notes.push("contact_on_file_but_no_consent_recorded");
    }
    return {
      action: "alert_owner_and_stay_silent",
      windowBlocked: true,
      // Loud, because this is the case where the customer gets nothing at all.
      // The alert is not a courtesy here, it is the only remaining action.
      ownerAlert: "loud",
      teamBoard,
      notes,
    };
  }

  // --- still open: will it survive long enough for the holding message to matter?
  const msUntilClose =
    input.lastCustomerMessageAt === null
      ? 0
      : input.lastCustomerMessageAt.getTime() + STANDARD_WINDOW_MS - input.now.getTime();
  const slaFiresAt = input.escalatedAt.getTime() + HOLDING_SLA_MS - input.now.getTime();

  if (isWindowGated(input.channel) && msUntilClose <= slaFiresAt && !input.hasContactOnFile) {
    // The window closes before the SLA fires. A holding message that lands and
    // is then unfollowable is worth less than a way to reach the customer at
    // all, so the last in-window act goes to contact capture.
    notes.push("window_closes_before_sla", `closes_in_ms_${Math.max(msUntilClose, 0)}`);
    return {
      action: "capture_contact_before_close",
      windowBlocked: false,
      ownerAlert: "normal",
      teamBoard,
      notes,
    };
  }

  if (waiting >= HOLDING_SLA_MS && !input.holdingMessageSent) {
    return {
      action: "send_holding_message",
      windowBlocked: false,
      ownerAlert: teamBoard === "amber" ? "loud" : "normal",
      teamBoard,
      notes,
    };
  }

  return {
    action: "wait",
    windowBlocked: false,
    ownerAlert: teamBoard === "amber" ? "loud" : "none",
    teamBoard,
    notes,
  };
}
